<form [formGroup]="formGroup">
  <div class="card-container">
    <pdq-collapsed #discountCollapsed [initialCollapsed]="true" [animate]="true">
      <pdq-collapsed-header>
        <div class="card-header" [class.collapsed]="discountCollapsed.collapsed">
          <pdq-heading element="h5">Add Discount</pdq-heading>
          <div class="card-header__right">
            <pdq-slide-toggle
              formControlName="hasShippingMethodDiscount"
              (click)="$event.stopPropagation()"
              (onToggle)="onToggleChange($event)">
            </pdq-slide-toggle>
            <pdq-collapsed-trigger [class.collapsed]="discountCollapsed.collapsed">
              <pdq-icon icon="arrow_down" color="var(--on-surface-light)"></pdq-icon>
            </pdq-collapsed-trigger>
          </div>
        </div>
      </pdq-collapsed-header>
      <pdq-collapsed-content>
        <pdq-divider></pdq-divider>
        <div class="card-body">
          <div class="description">
            The discount is applied after the shipping rate and additional costs have been added
          </div>

          @if (formGroup.get('hasShippingMethodDiscount')?.value) {
            <pdq-banner
              variant="attention"
              bannerTitle="Check that external discounts allow combining"
              [closable]="false"
              [content]="'If a shipping method uses a discount from outside PDQ, make sure it\'s set to combine with other discounts. Otherwise this discount won\'t apply correctly.'">
            </pdq-banner>

            <div class="discount-cards">
              @for (card of discountCards.controls; track card; let cardIndex = $index) {
                <div class="discount-card">
                  <pdq-collapsed
                    #cardCollapsed
                    [initialCollapsed]="true"
                    [animate]="true"
                    (onChange)="onCardCollapseChange($event, cardIndex)">
                    <pdq-collapsed-header>
                      <div class="card-row card-header-row">
                        <div class="row-pair">
                          <label class="row-label">Shipping Method</label>
                          <div class="row-control">
                            <pdq-dropdown
                              [options]="shippingMethodOptions"
                              [formControl]="card.get('shippingMethodName')"
                              [defaultOption]="shippingMethodDefaultOptions.get(card)"
                              placeholder="Pick Shipping Method"
                              [fullWidth]="true"
                              [enableSearch]="true"
                              [preserveWhitespace]="true"
                              [displayUnknownValue]="true"
                              [invalid]="
                                card.get('shippingMethodName')?.hasError('duplicateMethod') ||
                                card.get('shippingMethodName')?.hasError('methodNotAvailable') ||
                                (card.get('shippingMethodName')?.touched &&
                                  card.get('shippingMethodName')?.hasError('required'))
                              "
                              [invalidMsg]="
                                card.get('shippingMethodName')?.hasError('duplicateMethod')
                                  ? 'This shipping method and notes combination overlaps with another card'
                                  : card.get('shippingMethodName')?.hasError('methodNotAvailable')
                                    ? '\'' + card.get('shippingMethodName')?.value + '\' is no longer available'
                                    : 'Please select a shipping method'
                              "
                              (onChange)="onShippingMethodSelected($event, cardIndex)">
                            </pdq-dropdown>
                            @if (legacyDiscountWarningByCard.get(card)) {
                              <div class="legacy-discount-warning">
                                <pdq-icon icon="Attention" size="smaller" color="var(--warning-default)"></pdq-icon>
                                <span>
                                  Some of the selected "{{ card.get('shippingMethodName')?.value }}" methods already
                                  have a per-rate discount — the best discount for the customer will apply at checkout.
                                  <a
                                    routerLink="/convert/checkout-groups/methods/list"
                                    class="legacy-discount-warning__link"
                                    >Manage Rate Groups</a
                                  >
                                </span>
                              </div>
                            }
                          </div>
                        </div>
                        <div class="row-pair">
                          <div class="row-label-group">
                            <label class="row-label">Notes</label>
                            <pdq-tooltip #notesTooltip position="top">
                              <pdq-icon
                                icon="more_information"
                                size="small"
                                color="var(--on-surface-default)"
                                role="button"
                                tabindex="0"
                                [attr.aria-label]="noteMatchingTitle + '. ' + noteMatchingBody"
                                (focus)="notesTooltip.show()"
                                (blur)="notesTooltip.hide()"
                                (click)="notesTooltip.show()"
                                (keydown.enter)="notesTooltip.show()"
                                (keydown.space)="notesTooltip.show()"
                                (keydown.escape)="notesTooltip.hide()"></pdq-icon>
                              <ng-template #tip>
                                <div class="notes-tooltip">
                                  <span class="notes-tooltip__title">{{ noteMatchingTitle }}</span>
                                  <span>{{ noteMatchingBody }}</span>
                                </div>
                              </ng-template>
                            </pdq-tooltip>
                          </div>
                          <div class="row-control">
                            <pdq-dropdown
                              [options]="notesOptionsByCard.get(card) ?? []"
                              [defaultOption]="$any(notesOptionsByCard.get(card) ?? [])"
                              [multiSelect]="true"
                              [selectBy]="'checked'"
                              [enableSearch]="true"
                              [disabled]="!card.get('shippingMethodName')?.value"
                              [fullWidth]="true"
                              [preserveWhitespace]="true"
                              placeholder="Any note"
                              [multiSelectCountFormatter]="formatNotesCount"
                              (onMultiSelectChange)="onNotesChange($event, card)">
                            </pdq-dropdown>
                          </div>
                        </div>
                        <div class="row-actions">
                          <pdq-button
                            icon="trash"
                            [iconColor]="discountCards.length > 1 ? 'mario' : 'flint'"
                            variant="empty"
                            size="small"
                            [disabled]="discountCards.length <= 1"
                            (onClick)="removeDiscountCard(cardIndex)">
                          </pdq-button>
                          <pdq-collapsed-trigger [class.collapsed]="cardCollapsed.collapsed">
                            <pdq-icon icon="arrow_down" color="var(--on-surface-light)"></pdq-icon>
                          </pdq-collapsed-trigger>
                        </div>
                      </div>
                    </pdq-collapsed-header>
                    <pdq-collapsed-content>
                      <div class="ranges">
                        <div class="discount-hint">
                          Set in your store currency. Other currencies convert automatically
                        </div>
                        <div class="range-headers">
                          <div class="cart-range-header">Cart Range</div>
                          <div class="discount-header">Discount</div>
                        </div>
                        @for (range of $any(card.get('ranges')).controls; track range; let rangeIndex = $index) {
                          <div class="range-row">
                            <div class="cart-range-cell">
                              <pdq-input-arrows
                                [formControl]="range.get('minPrice')"
                                [symbol]="shopCurrencySign"
                                [min]="0"
                                [max]="range.get('maxPrice')?.value"
                                [invalid]="range.hasError('minExceedsMax')"
                                invalidMsg="Min must be less than max"
                                precision="2"
                                widthSize="fullwidth">
                              </pdq-input-arrows>
                              <span class="range-to">to</span>
                              <pdq-input-arrows
                                [formControl]="range.get('maxPrice')"
                                [symbol]="shopCurrencySign"
                                [min]="0"
                                [required]="false"
                                [invalid]="range.hasError('minExceedsMax')"
                                invalidMsg="Max must be greater than min"
                                precision="2"
                                placeholder="No limit"
                                widthSize="fullwidth">
                              </pdq-input-arrows>
                            </div>
                            <div class="discount-cell">
                              <pdq-input-arrows
                                [formControl]="range.get('discountAmount')"
                                [symbol]="getDiscountSymbol(range.get('discountType')?.value)"
                                [placeholder]="getDiscountPlaceholder(range.get('discountType')?.value)"
                                [min]="0"
                                [max]="range.get('discountType')?.value === 'percentage' ? 100 : undefined"
                                precision="2"
                                widthSize="fullwidth">
                              </pdq-input-arrows>
                              <pdq-dropdown
                                [options]="discountTypeOptions"
                                [formControl]="range.get('discountType')"
                                [fullWidth]="true"
                                (selectionChange)="onDiscountTypeChange(card, rangeIndex)">
                              </pdq-dropdown>
                            </div>
                            <div class="range-action">
                              <pdq-button
                                icon="trash"
                                [iconColor]="$any(card.get('ranges')).controls.length > 1 ? 'mario' : 'flint'"
                                variant="empty"
                                size="small"
                                [disabled]="$any(card.get('ranges')).controls.length <= 1"
                                (onClick)="removeRangeFromCard(card, rangeIndex)">
                              </pdq-button>
                            </div>
                          </div>
                        }
                        <div class="add-range">
                          <pdq-button
                            icon="Plus"
                            iconColor="var(--on-surface-default)"
                            labelColor="var(--on-surface-default)"
                            variant="empty"
                            size="small"
                            label="Add range"
                            [fullWidth]="true"
                            (onClick)="addRangeToCard(card)">
                          </pdq-button>
                        </div>
                      </div>
                    </pdq-collapsed-content>
                  </pdq-collapsed>
                </div>
              }

              <div class="add-card">
                <pdq-button
                  icon="Plus"
                  iconColor="var(--secondary-default)"
                  labelColor="var(--secondary-default)"
                  variant="empty"
                  size="medium"
                  label="Add Shipping method"
                  (onClick)="addDiscountCard()">
                </pdq-button>
              </div>
            </div>
          }
        </div>
      </pdq-collapsed-content>
    </pdq-collapsed>
  </div>
</form>
